import React, { FC, useEffect, useState } from 'react'
import { debounce, isEmpty } from 'lodash'
import { css } from '@emotion/react'
import SVG from 'react-inlinesvg'

import SelectTimezone from 'front-end/src/components/common/SelectTimezone'
import {
  Box,
  Flex,
  Text,
  HStack,
  VStack,
  ButtonPrimary,
  Modal,
  ButtonClean,
  Switch,
  Link,
  Checkbox
} from 'front-end/src/components'
import { LanguageDropdown } from 'front-end/src/components/common/language-dropdown/LanguageDropdown'
import {
  SupportedLocalesEnum,
  OrganisationStyles,
  EmbedDomains
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import InputColour from 'front-end/src/components/common/form/input/InputColour'
import { brandColors } from 'shared/basicPalette'

import { isColorDark } from 'shared/colorUtils'
import IconEmailTemplate from 'public/assets/img/email-template.svg'
import IconWebTemplate from 'public/assets/img/web-template.svg'

import Input from '../../../components/common/form/input/Input'
import isSubdomainAvailable from '../operations/isSubdomainAvailable'
import Label from '../../../components/common/form/label/Label'
import Loader from '../../../components/common/loader/Loader'
import validators from '../../../../../shared/validations'
import { AccountTabSection } from '../TabContentSection'
import { Upload } from '../../../components/common/form/upload/Upload'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'
import EmbedDomainsSelect from './EmbedDomainsSelect'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'

import './CustomiseTab.less'
import { t, Trans } from '@lingui/macro'
import IconBubble from 'front-end/src/components/common/icon/IconBubble'
import { useCustomizeTabQuery } from './CustomiseTab.codegen'
import {
  useCPDFeatureFlag,
  useFeatureFlags
} from 'front-end/src/app/hooks/useFeatureFlags'
import { NbSp } from '../../../utils/NbSp'

export interface ICustomiseTabForm {
  timezone: string
  default_user_locale: SupportedLocalesEnum
  name: string
  subdomain: string
  styles: OrganisationStyles
  can_students_update_profile: boolean
  hotjar_site_id: number | null
  hotjar_enabled: boolean
  cpd_switched_on: boolean
  link_unfurling_switched_on: boolean
  embed_domains: EmbedDomains
}

interface IProps {
  form: ICustomiseTabForm
  loading: boolean
  onFormChange: (form: ICustomiseTabForm) => Promise<void>
}

const CustomiseTab: FC<IProps> = (props) => {
  const { cpdAlias } = useCPDFeatureFlag()
  const { refetchFeatureFlags } = useFeatureFlags()
  const { form, loading, onFormChange } = props
  const [subdomainAvailable, setSubdomainAvailable] = useState(true)
  const [dirty, setDirty] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [localForm, setLocalForm] = useState<ICustomiseTabForm>()
  const [showDisableIframes, setShowDisableIframes] = useState(false)
  const { featureFlags } = useFeatureFlags()
  const { refetchOrgColors, theme } = useLooopTheme()

  const { data } = useCustomizeTabQuery()

  const colors = localForm?.styles?.colors

  useEffect(() => {
    setLocalForm(form)
  }, [form])

  if (isEmpty(localForm)) {
    return <Loader />
  }

  const debouncedCheckSubdomain = debounce(
    async (subdomainName: string): Promise<void> => {
      const isAvailable = await isSubdomainAvailable(subdomainName)
      setSubdomainAvailable(isAvailable)
    },
    1000
  )

  const handleLocalFormChange = (localForm: ICustomiseTabForm): void => {
    setLocalForm(localForm)
    setDirty(true)
  }

  const handleColorChange = (name: string, color: string): void => {
    handleLocalFormChange({
      ...(localForm as ICustomiseTabForm),
      styles: {
        colors: { ...localForm?.styles.colors, [name]: color }
      }
    })
  }

  const handleSubdomainChange = (
    subdomain: string,
    validName: boolean
  ): void => {
    if (validName) {
      debouncedCheckSubdomain(subdomain)
    }

    handleLocalFormChange({
      ...(localForm as ICustomiseTabForm),
      subdomain
    })
  }

  const handleUploadLogo = async (file: any): Promise<void> => {
    setUploadingLogo(true)
    setDirty(true)
    await onFormChange({
      ...form,
      styles: {
        logo: file,
        colors: localForm?.styles.colors
      }
    })
    setUploadingLogo(false)
  }

  const handleSaveClick = async (): Promise<void> => {
    const isCpdChanged = localForm?.cpd_switched_on !== form?.cpd_switched_on
    await onFormChange(localForm as ICustomiseTabForm)

    setDirty(false)
    refetchOrgColors()

    if (isCpdChanged) {
      await refetchFeatureFlags()
    }
  }

  const disableSubdomainChange =
    !!data?.admin?.organisation?.hasSSOBoundToSubdomain
  return (
    <AccountTabSection className="CustomiseTab">
      <div className="AccountPage__box">
        <h4>
          <Trans>Settings</Trans>
        </h4>
        <Label htmlFor="AccountPage__CustomiseTab__name">
          <Trans>Organisation</Trans>
        </Label>
        <Input
          maxLength={255}
          id="AccountPage__CustomiseTab__name"
          value={localForm?.name}
          onChange={(name) =>
            handleLocalFormChange({ ...localForm, name } as ICustomiseTabForm)
          }
        />
        <div>
          <Label htmlFor="AccountPage__CustomiseTab__team_name">
            <Trans>Team Name (domain)</Trans>
          </Label>
          <Flex w={'100%'} align="center">
            <Input
              id="AccountPage__CustomiseTab__team_name"
              disabled={disableSubdomainChange} // we don't want users changing their domain if they have active SSO like SAML
              className={subdomainAvailable ? 'invalid' : ''}
              value={localForm?.subdomain}
              pattern={validators.subdomain.source}
              onChange={(subdomain, event) => {
                if (event) {
                  handleSubdomainChange(subdomain, event.target.validity.valid)
                }
              }}
            />
            <em className="postfix">.looop.co</em>
          </Flex>

          {disableSubdomainChange && (
            <Box mt={'4px'} fontSize="xs">
              <Trans>To change, contact</Trans>{' '}
              <Link target="_blank" href="mailto:support@looop.co">
                support@looop.co
              </Link>
            </Box>
          )}
          {!subdomainAvailable && (
            <Text color={brandColors.red.mid} mt="8px">
              <Trans>This domain is already taken</Trans>
            </Text>
          )}
        </div>

        <div>
          <Label htmlFor="language">
            <Trans>Language</Trans>
          </Label>
          <LanguageDropdown
            inputId="language"
            className="react-select w50"
            value={localForm?.default_user_locale}
            onChange={(default_user_locale) => {
              handleLocalFormChange({
                ...localForm,
                default_user_locale
              } as ICustomiseTabForm)
            }}
          />
        </div>

        <div>
          <Label htmlFor="timezone">
            <Trans>Timezone</Trans>
          </Label>
          <SelectTimezone
            inputId="timezone"
            className="react-select w50"
            value={localForm?.timezone}
            onChange={(timezone) =>
              handleLocalFormChange({
                ...localForm,
                timezone
              } as ICustomiseTabForm)
            }
          />
        </div>
        <Text
          mt="8px"
          maxWidth="600px"
          color={theme.colors.grey['500']}
          fontSize="12px"
        >
          <Trans>
            Language and timezone changes will set the default for new user or
            users that haven’t set the language or the timezone in their
            profile.
          </Trans>
        </Text>
      </div>

      {featureFlags?.cpd?.enabled && (
        <div className="AccountPage__box CustomiseTab__allowCPD">
          <h4>
            <Trans>{cpdAlias} Hours Requirements</Trans>
          </h4>
          <Text mb="8px" className="text-description" maxWidth="600px">
            <Trans>
              This option allows learners to maintain their {cpdAlias} portfolio
              and upload evidence of {cpdAlias}.
            </Trans>
          </Text>
          <Checkbox
            marginTop={0}
            isChecked={localForm?.cpd_switched_on}
            onChange={() =>
              handleLocalFormChange({
                ...localForm,
                cpd_switched_on: !localForm.cpd_switched_on
              } as ICustomiseTabForm)
            }
          >
            <Trans>Allow {cpdAlias} Requirements</Trans>
          </Checkbox>
        </div>
      )}

      <div className="AccountPage__box">
        <h4>
          <Trans>Branding</Trans>
        </h4>
        <Text mb="32px" className="text-description">
          <Trans>Change the appearance to fit with your brand styling.</Trans>
        </Text>
        <HStack
          mb="32px"
          spacing="12px"
          css={css`
            #Navigation,
            #Navigation2 {
              fill: ${colors?.navbarBackground ??
              theme.colors.navbarBackground};
            }

            #NavLink,
            #NavLink2,
            #NavLink3 {
              fill: ${colors?.navbarLink ?? theme.colors.navbarLink};
            }

            #NavLinkSearch {
              opacity: 0.2;
              fill: ${isColorDark(
                colors?.navbarBackground ?? theme.colors.navbarBackground
              )
                ? brandColors.white
                : brandColors.grey.dark};
            }

            #Button,
            #Button2 {
              fill: ${colors?.buttonPrimary ??
              theme.colors.buttonPrimary.default};
            }

            #Button-text,
            #Button2-text {
              fill: ${isColorDark(
                colors?.buttonPrimary ?? theme.colors.buttonPrimary.default
              )
                ? brandColors.white
                : brandColors.grey.dark};
            }

            #Link,
            #Link2 {
              fill: ${colors?.linkText ?? theme.colors.linkText};
              opacity: 0.66;
            }
          `}
        >
          <SVG aria-label={t`Web template showcase`} src={IconWebTemplate} />
          <SVG
            aria-label={t`Email template showcase`}
            src={IconEmailTemplate}
          />
        </HStack>
        <h4>Colours</h4>
        <Text mb="8px" className="text-description">
          <Trans>
            Select the desired colours for each element. Some colours may be
            selected automatically.
          </Trans>
        </Text>
        <Flex mb="32px">
          <VStack>
            <HStack spacing="28px">
              <Flex direction="column">
                <Label htmlFor="background-color">
                  <Trans>Nav bar background</Trans>
                </Label>
                <InputColour
                  inputId="background-color"
                  colorHex={
                    colors?.navbarBackground ?? theme.colors.navbarBackground
                  }
                  onChange={(color) => {
                    handleColorChange('navbarBackground', color)
                  }}
                />
              </Flex>
              <Flex direction="column">
                <Label htmlFor="links-color">
                  <Trans>Nav bar links</Trans>
                </Label>
                <InputColour
                  inputId="links-color"
                  colorHex={colors?.navbarLink ?? theme.colors.navbarLink}
                  onChange={(color) => {
                    handleColorChange('navbarLink', color)
                  }}
                />
              </Flex>
            </HStack>
            <HStack spacing="28px">
              <Flex direction="column">
                <Label htmlFor="primary-color">
                  <Trans>Primary button</Trans>
                </Label>
                <InputColour
                  inputId="primary-color"
                  colorHex={
                    colors?.buttonPrimary ?? theme.colors.buttonPrimary.default
                  }
                  onChange={(color) => {
                    handleColorChange('buttonPrimary', color)
                  }}
                />
              </Flex>
              <Flex direction="column">
                <Label htmlFor="links-color">
                  <Trans>Text links</Trans>
                </Label>
                <InputColour
                  inputId="links-color"
                  colorHex={colors?.linkText ?? theme.colors.linkText}
                  onChange={(color) => {
                    handleColorChange('linkText', color)
                  }}
                />
              </Flex>
            </HStack>
          </VStack>
        </Flex>

        <h4>
          <Trans>Logos</Trans>
        </h4>
        <Box mb="16px">
          <Label>
            <Trans>Logo file</Trans>
          </Label>
          {form.styles?.logo && (
            <Box
              className="logo"
              css={css`
                max-width: 600px;
                text-align: center;
                margin-bottom: 16px;
                background-image: linear-gradient(
                    45deg,
                    #eeeeee 25%,
                    transparent 25%
                  ),
                  linear-gradient(-45deg, #eeeeee 25%, transparent 25%),
                  linear-gradient(45deg, transparent 75%, #eeeeee 75%),
                  linear-gradient(-45deg, transparent 75%, #eeeeee 75%);
                background-size: 20px 20px;
                background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
              `}
            >
              <img
                src={form.styles?.logo.original ?? ''}
                alt={t`Company logo`}
              />
            </Box>
          )}
          <Upload
            title={t`Choose image`}
            accept="image/png, image/jpeg"
            isLoading={uploadingLogo}
            onChange={(event) => {
              const files = event.currentTarget.files
              if (files && files.length) {
                handleUploadLogo(files[0])
              }
            }}
          />
        </Box>
      </div>

      <div className="AccountPage__box CustomiseTab__allowUpdate">
        <h4>
          <Trans>Allow all users to update their own profile information</Trans>
        </h4>
        <Text mb="8px" className="text-description">
          <Trans>
            Selecting this option will allow all users to update their own
            profile information, including name, email and location.
          </Trans>
          <NbSp />
          <Trans>
            This will be also applied on the Additional information.
          </Trans>
        </Text>
        <Switch
          isChecked={localForm?.can_students_update_profile}
          onChange={() =>
            handleLocalFormChange({
              ...localForm,
              can_students_update_profile:
                !localForm?.can_students_update_profile
            } as ICustomiseTabForm)
          }
        />
      </div>

      {featureFlags?.link_unfurling?.enabled && (
        <div className="AccountPage__box minimizeSpacing">
          <h4>
            <Trans>Allow link previews</Trans>
          </h4>
          <Text className="text-description">
            <Trans>
              Link previews (also known as unfurling) allows users to see what
              was shared in internal or external social media platforms.
            </Trans>
          </Text>
          <Text mt={0} mb="8px" className="text-description">
            <Trans>
              <strong>Notice:</strong> Certain content such as title, image, and
              description will be visible online publicly for all shared links.{' '}
              <Link
                href="http://support.looop.co/en/articles/5016156-link-previews"
                target="_blank"
              >
                Learn more
              </Link>
            </Trans>
          </Text>
          <Switch
            isChecked={localForm?.link_unfurling_switched_on}
            onChange={() =>
              handleLocalFormChange({
                ...localForm,
                link_unfurling_switched_on:
                  !localForm?.link_unfurling_switched_on
              } as ICustomiseTabForm)
            }
          />
        </div>
      )}

      <div className="AccountPage__box minimizeSpacing">
        <h4>
          <Trans>Allow iFrame embed</Trans>
        </h4>
        <Text mb="8px" className="text-description">
          <Trans>
            This will allow content editors to add elements from external
            sources using the iframe code. For security reasons, only allowed
            items will be processed.
          </Trans>
        </Text>
        <Switch
          isChecked={!!localForm?.embed_domains.enabled}
          onChange={() => {
            if (localForm?.embed_domains.enabled) {
              setShowDisableIframes(true)
            } else {
              handleLocalFormChange({
                ...localForm,
                embed_domains: {
                  enabled: true,
                  domains: localForm?.embed_domains.domains || []
                }
              } as ICustomiseTabForm)
            }
          }}
        />
        <Modal
          newDesign
          className={'ModalDisableIframe'}
          header={t`Disable iFrames`}
          show={showDisableIframes}
          controls={[
            <ButtonClean
              onClick={() => {
                handleLocalFormChange({
                  ...localForm,
                  embed_domains: {
                    enabled: false,
                    domains: localForm?.embed_domains.domains || []
                  }
                } as ICustomiseTabForm)
                setShowDisableIframes(false)
              }}
              key={1}
            >
              <Trans>OK</Trans>
            </ButtonClean>,
            <ButtonClean onClick={() => setShowDisableIframes(false)} key={2}>
              <Trans>Cancel</Trans>
            </ButtonClean>
          ]}
        >
          <div className={'Modal__relatedItems'}>
            <div className={'Modal__relatedItems__column'}>
              <IconBubble size="medium" />
            </div>
            <div className={'Modal__relatedItems__column'}>
              <p>
                <Trans>
                  Note that it will not be possible to add new iframe elements
                  to resources.
                </Trans>
              </p>
            </div>
          </div>
        </Modal>
        {localForm?.embed_domains.enabled && (
          <Box mt="20px">
            <Text mb="8px" className="text-description">
              <Trans>iFrame embed allowlist</Trans>
            </Text>
            <EmbedDomainsSelect
              onFormChange={(domains: string[]) => {
                handleLocalFormChange({
                  ...localForm,
                  embed_domains: {
                    enabled: localForm?.embed_domains.enabled || false,
                    domains
                  }
                })
              }}
              domains={localForm?.embed_domains.domains || []}
            />
            <Text fontSize={fontSizes.sm} mt={2} color={brandColors.grey.mid}>
              <Trans>Type to add domain into allowlist</Trans>
            </Text>
          </Box>
        )}
      </div>

      {localForm?.hotjar_enabled && (
        <div className="AccountPage__box minimizeSpacing">
          <h4>
            <Trans>Hotjar integration</Trans>
          </h4>
          <Text mb="8px" className="text-description">
            <Trans>
              To activate Hotjar tracking on your Looop account, please setup
              here your Hotjar Site ID you received when you created Hotjar
              account.
            </Trans>
          </Text>
          <Label htmlFor="AccountPage__CustomiseTab__name">
            <Trans>Site ID</Trans>
          </Label>
          <Input
            type="number"
            id="AccountPage__CustomiseTab__name"
            value={localForm.hotjar_site_id || ''}
            onChange={(hotjar_site_id) =>
              handleLocalFormChange({
                ...localForm,
                hotjar_site_id: parseInt(hotjar_site_id, 10) || null
              })
            }
          />
        </div>
      )}

      <Box className="save-button">
        <ButtonPrimary
          disabled={!dirty}
          isLoading={loading}
          onClick={handleSaveClick}
        >
          <Trans>Save</Trans>
        </ButtonPrimary>
      </Box>
    </AccountTabSection>
  )
}

export default CustomiseTab
